/**
 * SidebarSprintPicker — Compact sprint picker rendered in the sidebar.
 *
 * Reads sprint data independently using the same react-query cache key
 * as SprintContext (['sprints', 'all', projectId]). When the user picks
 * a sprint, it sets the ?sprint= URL param which SprintContext auto-reads.
 *
 * Only renders on project-scoped routes when sprints exist.
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, CheckCircle2, XCircle } from 'lucide-react';
import { sprintApi, type Sprint } from '../../api/sprint';
import { useProject } from '../../contexts/ProjectContext';

const STATUS_CONFIG: Record<string, { dot: string; label: string; animate?: boolean }> = {
  building:  { dot: 'bg-coral-500', label: 'Building', animate: true },
  planning:  { dot: 'bg-neutral-400', label: 'Planning' },
  completed: { dot: 'bg-emerald-500', label: 'Completed' },
  failed:    { dot: 'bg-red-500', label: 'Failed' },
};

function formatRelativeDate(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function SidebarSprintPicker() {
  const { project } = useProject();
  const projectId = project?.id;
  const [searchParams, setSearchParams] = useSearchParams();
  const sprintParam = searchParams.get('sprint');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Same cache key as SprintContext — reads from shared cache
  const { data, isLoading } = useQuery({
    queryKey: ['sprints', 'all', projectId],
    queryFn: () => sprintApi.list(projectId!),
    enabled: !!projectId,
    refetchInterval: 10_000,
    staleTime: 5_000,
  });

  const allSprints = data?.sprints ?? [];

  // Group by status (mirrors SprintContext logic)
  const groups = useMemo(() => {
    const g: Record<string, Sprint[]> = { building: [], planning: [], completed: [], failed: [] };
    for (const s of allSprints) {
      if (s.status in g) g[s.status].push(s);
    }
    g.planning.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    g.completed.sort((a, b) =>
      new Date(b.completedAt || b.createdAt).getTime() - new Date(a.completedAt || a.createdAt).getTime()
    );
    g.failed.sort((a, b) =>
      new Date(b.failedAt || b.createdAt).getTime() - new Date(a.failedAt || a.createdAt).getTime()
    );
    return g;
  }, [allSprints]);

  // Resolve selected sprint (same algorithm as SprintContext)
  const selectedSprint = useMemo<Sprint | null>(() => {
    if (allSprints.length === 0) return null;
    if (sprintParam) {
      const found = allSprints.find(s => s.id === sprintParam);
      if (found) return found;
    }
    if (groups.building.length > 0) return groups.building[0];
    const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
    const recentPlanning = groups.planning.find(s => Date.now() - new Date(s.createdAt).getTime() < SEVEN_DAYS);
    if (recentPlanning) return recentPlanning;
    const terminal = [...groups.completed, ...groups.failed].sort((a, b) =>
      new Date(b.completedAt || b.failedAt || b.createdAt).getTime() -
      new Date(a.completedAt || a.failedAt || a.createdAt).getTime()
    );
    return terminal[0] ?? allSprints[0] ?? null;
  }, [allSprints, sprintParam, groups]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleSelect = (id: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('sprint', id);
      return next;
    }, { replace: true });
    setOpen(false);
  };

  // Don't render if no project or no sprints
  if (!projectId || isLoading || allSprints.length === 0) return null;

  const statusCfg = selectedSprint ? STATUS_CONFIG[selectedSprint.status] : null;

  // Show most actionable sprints first: building → failed → planning → completed
  const groupList = [
    { status: 'building', sprints: groups.building },
    { status: 'failed', sprints: groups.failed },
    { status: 'planning', sprints: groups.planning },
    { status: 'completed', sprints: groups.completed },
  ].filter(g => g.sprints.length > 0);

  return (
    <div ref={ref} className="px-4 pb-1">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-left hover:bg-surface-subtle/80 transition-colors group"
      >
        {/* Status dot */}
        {statusCfg && (
          <span className="relative flex h-1.5 w-1.5 shrink-0">
            {statusCfg.animate && (
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${statusCfg.dot} opacity-75`} />
            )}
            <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${statusCfg.dot}`} />
          </span>
        )}
        <span className="flex-1 text-[11px] font-medium text-muted group-hover:text-text truncate transition-colors">
          {selectedSprint?.name || 'Select sprint'}
        </span>
        <ChevronDown size={11} className={`text-subtle transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-4 right-4 z-50 mt-1 bg-white rounded-lg shadow-lg border border-line py-1 max-h-[480px] overflow-y-auto">
            {groupList.map(group => {
              const cfg = STATUS_CONFIG[group.status];
              return (
                <div key={group.status}>
                  <div className="px-3 py-1.5 text-[9px] font-bold uppercase tracking-widest text-subtle">
                    {cfg.label}
                  </div>
                  {group.sprints.map(sprint => {
                    const isSelected = sprint.id === selectedSprint?.id;
                    const dateStr = sprint.completedAt || sprint.failedAt || sprint.startedAt || sprint.createdAt;
                    return (
                      <button
                        key={sprint.id}
                        onClick={() => handleSelect(sprint.id)}
                        className={`w-full text-left px-3 py-2 text-[12px] transition-colors ${
                          isSelected
                            ? 'bg-coral-50 text-coral-700 font-medium'
                            : 'text-muted hover:bg-surface-muted'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {sprint.status === 'building' && (
                            <span className="relative flex h-1.5 w-1.5 shrink-0">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-coral-400 opacity-75" />
                              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-coral-500" />
                            </span>
                          )}
                          {sprint.status === 'completed' && <CheckCircle2 size={11} className="text-emerald-500 shrink-0" />}
                          {sprint.status === 'failed' && <XCircle size={11} className="text-red-500 shrink-0" />}
                          {sprint.status === 'planning' && <span className="w-1.5 h-1.5 rounded-full bg-neutral-400 shrink-0" />}
                          <span className="font-medium truncate">{sprint.name}</span>
                        </div>
                        <div className="text-[10px] text-subtle mt-0.5 pl-[18px]">
                          {sprint.epicIds.length} epic{sprint.epicIds.length !== 1 ? 's' : ''}
                          {' · '}
                          {formatRelativeDate(dateStr)}
                        </div>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
